import { Component, signal, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ClientService } from '../../../core/services/client.service';
import { Client } from '../../../core/models/models';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-client-list',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, ConfirmDialogComponent, MatPaginatorModule, MatSortModule, MatIconModule, MatSnackBarModule],
  templateUrl: `client-list.component.html`,
  styleUrls: [`client-list.component.css`]
})
export class ClientListComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  clientService = inject(ClientService);
  private router = inject(Router);
  Math = Math;

  clients = signal<Client[]>([]);
  photoUrls = signal<Record<number, string>>({});
  totalElements = signal(0);
  totalPages = signal(0);
  page = signal(0);
  size = signal(10);
  searchQuery = '';
  sortField = 'denomination';
  sortDirection = 'asc';

  private searchSubject = new Subject<string>();

  showDeleteModal = signal(false);
  clientToDelete = signal<Client | null>(null);
  showArchived = signal(false);

  constructor(private snackBar: MatSnackBar) {}

  ngOnInit() {
    this.loadClients();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadClients();
    });
  }

  ngOnDestroy() {
    this.revokePhotoUrls();
  }

  loadClients() {
    const sortParam = `${this.sortField},${this.sortDirection}`;
    this.clientService.getClients(this.searchQuery, this.page(), this.size(), sortParam, this.showArchived())
      .subscribe(response => {
        this.revokePhotoUrls();
        this.clients.set(response.content);
        this.totalElements.set(response.totalElements);
        this.totalPages.set(response.totalPages);
        this.loadClientPhotos(response.content);
      });
  }

  getPhotoUrl(clientId: number): string | null {
    return this.photoUrls()[clientId] ?? null;
  }

  private loadClientPhotos(clients: Client[]) {
    for (const client of clients) {
      if (!client.id || !client.photo) {
        continue;
      }

      this.clientService.getPhotoBlob(client.id).subscribe({
        next: (blob) => {
          const nextUrl = URL.createObjectURL(blob);
          this.photoUrls.update((urls) => {
            const previousUrl = urls[client.id!];
            if (previousUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(previousUrl);
            }
            return {
              ...urls,
              [client.id!]: nextUrl
            };
          });
        },
        error: () => {
          this.photoUrls.update((urls) => {
            const nextUrls = { ...urls };
            delete nextUrls[client.id!];
            return nextUrls;
          });
        }
      });
    }
  }

  private revokePhotoUrls() {
    for (const url of Object.values(this.photoUrls())) {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }
    this.photoUrls.set({});
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  onSortChange(sortState: Sort) {
    this.sortField = sortState.active;
    this.sortDirection = sortState.direction || 'asc';
    this.page.set(0);
    this.loadClients();
  }

  onToggleArchived() {
    this.showArchived.update(v => !v);
    this.page.set(0);
    this.loadClients();
  }

  onRowClick(client: Client) {
    if (client.id) {
      this.router.navigate(['/clients/edit', client.id]);
    }
  }

  onSort(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.loadClients();
  }

  onPageChange(event: PageEvent) {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadClients();
  }

  getSortIcon(field: string) {
    if (this.sortField !== field) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  onDelete(client: Client) {
    this.clientToDelete.set(client);
    this.showDeleteModal.set(true);
  }

  confirmDelete() {
    const client = this.clientToDelete();
    if (client && client.id) {
      this.clientService.deleteClient(client.id).subscribe({
        next: () => {
          this.loadClients();
          this.cancelDelete();
        },
        error: (err) => {
          this.snackBar.open('Erreur lors de la suppression du client.', 'Fermer', { duration: 3000 });
          this.cancelDelete();
        }
      });
    }
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
    this.clientToDelete.set(null);
  }
}
